"use client";

import ServicePageMeta from "@/components/common/seo/service-page-meta";
import ScreenLayout from "@/components/layout/screen-layout";
import type { CompanyLegalPageContent } from "@/lib/dto/legal/types";

type CompanyLegalPageProps = {
  content: CompanyLegalPageContent;
};

export default function CompanyLegalPage({ content }: CompanyLegalPageProps) {
  return (
    <>
      <ServicePageMeta
        title={content.metaTitle}
        description={content.metaDescription}
        keywords={[
          "Devkrest",
          content.titleAccent.toLowerCase(),
          "software development",
          "Ahmedabad",
          "India",
        ]}
        serviceType="Legal"
        url={content.url}
        image="/images/privacy/privacy-policy-hero.webp"
      />
      <main className="min-h-screen bg-white">
        <div className="relative overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-white pt-40 pb-20 md:pt-44 lg:pt-48">
          <div className="absolute inset-0 w-full h-full">
            <div className="absolute inset-0 bg-[linear-gradient(rgba(82,49,231,0.02)_1.6px,transparent_1.6px),linear-gradient(to_right,rgba(82,49,231,0.02)_1.6px,transparent_1.6px)] bg-[size:24px_24px]" />
            <div className="absolute top-20 left-10 h-20 w-20 rounded-full bg-gradient-to-r from-purple-400/20 to-blue-400/20 opacity-50 blur-xl" />
            <div className="absolute bottom-20 right-10 h-32 w-32 rounded-full bg-gradient-to-r from-blue-400/20 to-purple-400/20 opacity-50 blur-xl" />
          </div>

          <div className="relative z-10">
            <ScreenLayout>
              <div className="mx-auto max-w-4xl space-y-6 text-center">
                <h1 className="text-4xl font-semibold leading-[1.1] sm:text-5xl md:text-6xl">
                  <span className="text-gray-900">{content.title}</span>{" "}
                  <span className="bg-gradient-to-r from-[#5231E7] to-[#764AF1] bg-clip-text text-transparent">
                    {content.titleAccent}
                  </span>
                </h1>
                <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 md:text-xl">
                  {content.subtitle}
                </p>
              </div>
            </ScreenLayout>
          </div>
        </div>

        <div className="py-12 md:py-16">
          <ScreenLayout>
            <div className="mx-auto max-w-4xl">
              <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm md:p-12">
                <div className="prose prose-lg max-w-none">
                  {content.sections.map((section, index) => (
                    <div key={section.id} className="mb-12">
                      <h2 className="mb-6 flex items-center text-2xl font-bold text-gray-900 md:text-3xl">
                        <span className="mr-4 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-[#5231E7] to-[#764AF1] text-sm font-bold text-white">
                          {index + 1}
                        </span>
                        {section.title.replace(/^\d+\.\s*/, "")}
                      </h2>

                      {section.paragraphs?.map((paragraph) => (
                        <p
                          key={paragraph}
                          className="mb-4 leading-relaxed text-gray-700"
                        >
                          {paragraph}
                        </p>
                      ))}

                      {section.bullets ? (
                        <ul className="ml-4 list-inside list-disc space-y-2 leading-relaxed text-gray-700">
                          {section.bullets.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ))}
                </div>

                <nav
                  className="mt-8 border-t border-gray-100 pt-8"
                  aria-label="Related legal pages"
                >
                  <p className="mb-3 text-sm font-medium text-gray-900">
                    Related legal pages
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <a
                      href="/privacy-policy"
                      className="font-medium text-[#5231E7] hover:underline"
                    >
                      Privacy Policy
                    </a>
                    {content.url !== "/terms-and-conditions" ? (
                      <a
                        href="/terms-and-conditions"
                        className="font-medium text-[#5231E7] hover:underline"
                      >
                        Terms & Conditions
                      </a>
                    ) : null}
                    {content.url !== "/refund-cancellation-policy" ? (
                      <a
                        href="/refund-cancellation-policy"
                        className="font-medium text-[#5231E7] hover:underline"
                      >
                        Refund & Cancellation Policy
                      </a>
                    ) : null}
                  </div>
                </nav>
              </div>
            </div>
          </ScreenLayout>
        </div>
      </main>
    </>
  );
}
